import React, { useState } from "react";
import {
    Navbar,
    Nav,
    Container,
    Col,
    Button,
    Offcanvas,
    Accordion,
    NavDropdown,
} from "react-bootstrap";
import { FaBars, FaChevronDown } from "react-icons/fa";
import { Theme } from "../../shared/theme";
import { checkPermission } from "../../shared/utils";
import { useMediaQuery } from "../../context/MediaQueryContext";
import { routes } from "../../routes/index";
import "./styles.css";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Language, LocalStorageKey, Pages } from "../../shared/enums";

const Layout = ({ children, props }) => {
    const { isSmallScreen } = useMediaQuery();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(
        isSmallScreen ? false : true
    );
    const [userData] = useState(
        JSON.parse(localStorage.getItem(LocalStorageKey.USER_DATA))
    );

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const onLanguageChange = (languageId) => {
        i18n.changeLanguage(languageId);
        localStorage.setItem(LocalStorageKey.LANGUAGE, languageId);
    };

    const logout = () => {
        localStorage.removeItem(LocalStorageKey.TOKEN);
        localStorage.removeItem(LocalStorageKey.USER_DATA);
        navigate(Pages.Login);
    };

    const handleMenuLinkClick = (event, path) => {
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        event.preventDefault();
        navigate(path);

        if (isSmallScreen) {
            setIsSidebarVisible(false);
        }
    };

    const renderMenuItems = (routesArray, isOffcanvas) => {
        return routesArray
            .filter((route) => route.menu && checkPermission(route?.protection))
            .map((route) => {
                const isActive = (route?.menu?.selected ?? [])?.some(e => matchPath(e, location?.pathname))

                const textColor = isOffcanvas ? "text-black" : "text-white";
                const selectedClass = isActive ? "selected" : "";

                if (route?.menu?.children && route?.menu?.children?.length > 0) {
                    return (
                        <Accordion key={route.path} className="mb-2" alwaysOpen={true} defaultActiveKey={[route.path]}>
                            <Accordion.Item eventKey={route.path} className="border-0">
                                <Accordion.Header className={`d-flex align-items-center mb-0 ${selectedClass} ${textColor}`}>
                                    {route.menu.icon}
                                    <span className="ms-2">{t(route.menu.label)}</span>
                                    {!isSmallScreen && <FaChevronDown className="ms-auto accordion-icon" />}
                                </Accordion.Header>
                                <Accordion.Body className="p-0">
                                    <Nav className="flex-column">
                                        {route.menu.children.map((childRoute) => {
                                            if (!childRoute.menu || !checkPermission(childRoute?.protection)) return null;
                                            const isChildActive = (childRoute?.menu?.selected ?? [])?.some(e => matchPath(e, location?.pathname))
                                            const childSelectedClass = isChildActive ? "selected" : "";

                                            return (
                                                <Nav.Link
                                                    key={childRoute.path}
                                                    href={childRoute.path}
                                                    onClick={(event) => handleMenuLinkClick(event, childRoute.path)}
                                                    className={`${childSelectedClass} ${textColor} d-flex align-items-center`}
                                                    style={{ paddingLeft: "2rem" }}
                                                >
                                                    {childRoute.menu.icon}
                                                    <span className="ms-2">{t(childRoute.menu.label)}</span>
                                                </Nav.Link>
                                            );
                                        })}
                                    </Nav>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    );
                } else {
                    return (
                        <Nav.Link
                            key={route.path}
                            href={route.path}
                            onClick={(event) => handleMenuLinkClick(event, route.path)}
                            className={`${selectedClass} ${textColor} d-flex align-items-center mb-2`}
                        >
                            {route.menu.icon}
                            <span className="ms-2">{t(route.menu.label)}</span>
                        </Nav.Link>
                    );
                }
            });
    };

    return (
        <div className="d-flex flex-column h-100 overflow-hidden">
            <Navbar
                variant="dark"
                expand="lg"
                className="px-3 layout"
                style={{ background: Theme.primary, flexWrap: "nowrap" }}
            >
                <Navbar.Brand className="d-flex align-items-center">
                    <Button variant={"light"} onClick={toggleSidebar} size="sm">
                        <FaBars />
                    </Button>
                    <img
                        onClick={() => navigate(Pages.Default)}
                        src="/logo-text.png"
                        alt="logo-text"
                        className="ms-3"
                        style={{ height: "28px" }}
                    />
                </Navbar.Brand>
                <Nav className="ms-auto d-flex align-items-center flex-row">
                    <NavDropdown title={i18n.language.toUpperCase()} active="true">
                        <NavDropdown.Item onClick={() => onLanguageChange(Language.ENGLISH)}>
                            English
                        </NavDropdown.Item>
                        <NavDropdown.Item onClick={() => onLanguageChange(Language.CHINESE)}>
                            Chinese
                        </NavDropdown.Item>
                        <NavDropdown.Item onClick={() => onLanguageChange(Language.MELAYU)}>
                            Melayu
                        </NavDropdown.Item>
                    </NavDropdown>
                    <NavDropdown
                        className={"fullname-container"}
                        title={<span className="fullname">{userData?.fullName}</span>}
                        active="true"
                    >
                        <NavDropdown.Item>{t("CHANGE_PASSWORD")}</NavDropdown.Item>
                        <NavDropdown.Divider />
                        <NavDropdown.Item className="signout" onClick={logout}>
                            {t("LOGOUT")}
                        </NavDropdown.Item>
                    </NavDropdown>
                </Nav>
            </Navbar>
            {isSmallScreen && (
                <Offcanvas
                    show={isSidebarVisible}
                    onHide={() => setIsSidebarVisible(false)}
                >
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>Menu</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body className={"p-0"}>
                        <Nav className="flex-column">{renderMenuItems(routes, true)}</Nav>
                    </Offcanvas.Body>
                </Offcanvas>
            )}
            <Container fluid className="d-flex flex-grow-1 p-0 mw-100 h-100">
                {isSidebarVisible && !isSmallScreen && (
                    <Col
                        md={2}
                        className="bg-dark text-white sidebar px-2 pt-3 d-flex flex-column h-100 layout overflow-auto"
                        style={{ width: "240px", paddingBottom: "60px" }}
                    >
                        <Nav className="flex-column">{renderMenuItems(routes, false)}</Nav>
                    </Col>
                )}
                <div className="overflow-hidden w-100 h-100">
                    <div className="overflow-auto" style={{ height: "calc(100vh - 57px)" }}>
                        <div className={props.padding ? "p-4" : "p-0"}>
                            {children}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default Layout;
